import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useDispatch } from 'react-redux';
import { updateUserServices } from '../../redux/slices/crmSlice';
import toast from 'react-hot-toast';

const AVAILABLE_SERVICES = [
  { id: 'weightgain', label: 'Weight Gain' },
  { id: 'weightloss', label: 'Weight Loss' },
  { id: 'childnutrition', label: 'Child Nutrition' },
  { id: 'generalhealth', label: 'General Health' }
];

const ServicesModal = ({ isOpen, onClose, user }) => {
  const dispatch = useDispatch();
  const [selectedServices, setSelectedServices] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize with user's current services
  useEffect(() => {
    if (isOpen && user) {
      // Handle both string and array formats
      if (typeof user.services === 'string') {
        setSelectedServices([user.services]);
      } else if (Array.isArray(user.services)) {
        setSelectedServices(user.services);
      } else {
        setSelectedServices([]);
      }
    }
  }, [isOpen, user]);

  const handleToggleService = (serviceId) => {
    setSelectedServices(prev => {
      if (prev.includes(serviceId)) {
        return prev.filter(s => s !== serviceId);
      } else {
        return [...prev, serviceId];
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedServices.length === 0) {
      toast.error('Please select at least one service');
      return;
    }

    setIsSubmitting(true);

    try {
      await dispatch(updateUserServices({
        userId: user.id,
        services: selectedServices
      })).unwrap();

      toast.success('Services updated successfully!');
      onClose();
    } catch (error) {
      console.error('Error updating services:', error);
      toast.error('Failed to update services');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-lg mx-4 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden"
        >
          {/* Header */}
          <div className="p-4 border-b border-slate-100 bg-slate-50">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                  Update Services
                </h3>
                <h2 className="text-xl font-black text-slate-900 mt-1">Edit Services</h2>
                <p className="text-sm text-slate-500 font-medium mt-1">
                  for {user?.name} ({user?.number})
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <XMarkIcon className="h-5 w-5 text-slate-500 hover:text-slate-900" />
              </button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-4">
            <div className="space-y-3">
              <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-3">
                Select Services <span className="text-red-500">*</span>
              </label>

              {AVAILABLE_SERVICES.map((service) => (
                <div
                  key={service.id}
                  onClick={() => handleToggleService(service.id)}
                  className={`flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    selectedServices.includes(service.id)
                      ? 'border-blue-500 bg-blue-50 shadow-sm'
                      : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  <span className={`font-black text-sm ${
                    selectedServices.includes(service.id) ? 'text-blue-700' : 'text-slate-900'
                  }`}>
                    {service.label}
                  </span>
                  <div className={`w-5 h-5 rounded-lg border-2 flex items-center justify-center transition-all ${
                    selectedServices.includes(service.id)
                      ? 'border-blue-500 bg-blue-500'
                      : 'border-slate-300'
                  }`}>
                    {selectedServices.includes(service.id) && (
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-slate-100">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-600 hover:bg-slate-100 transition-all"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || selectedServices.length === 0}
                className="px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest bg-blue-600 text-white hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-lg"
              >
                {isSubmitting ? (
                  <>
                    <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Updating...
                  </>
                ) : (
                  'Update Services'
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ServicesModal;

